import { useWebDocumentTitle } from '@/core/hooks/useWebDocumentTitle';
import { LanguageToggle, Typography } from '@/components/atoms';
import { getColors } from '@/core/constants/uiColors';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation } from '@/core/hooks';
import {
  LANDING_PAGE_ACTION,
  LANDING_PAGE_SCREEN,
} from '@/core/constants/dynatraceCatalog';
import {
  landingSpaceDetailProperties,
  trackCatalogEvent,
} from '@/core/services/trackDynatraceCatalog';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { useWebViewport } from '@/core/providers/WebViewportProvider';
import { webViewDirProps } from '@/core/utils/layoutDirection';
import {
  formatPhoneNumberDisplay,
  openPhoneDialer,
} from '@/core/utils/phoneUtils';
import { OverlayScrollView } from '@/modules/properties-web/components/OverlayScrollView';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Image,
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  View,
  type DimensionValue,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { BloomBreadcrumbChevronIcon } from '../components/BloomBreadcrumbChevronIcon';
import { BloomCmsIcon } from '../components/BloomCmsIcon';
import { BloomSpaceCard } from '../components/BloomSpaceCard';
import { BloomSpacePhoneIcon } from '../components/BloomSpacePhoneIcon';
import { BloomWordmark } from '../components/BloomWordmark';
import {
  BLOOM_UNIVERSAL_HUB_PATH,
  BLOOM_UNIVERSAL_PROPERTIES_GUEST_PATH,
  BLOOM_UNIVERSAL_PROPERTIES_SPACE_KEY,
} from '../constants/routes';
import { useBloomUniversalSpaceContent } from '../hooks/useBloomUniversalHubContent';
import type {
  BloomUniversalHubContent,
  BloomUniversalSpaceCardContent,
  BloomUniversalSpaceContent,
} from '../types/bloomUniversalContent';
import { resolveBloomUniversalImageUrl } from '../utils/resolveImageUrl';

const bloomColors = getColors();

const PHONE_MAX = 768;
/**
 * Four cards in a row only at desktop width.
 * Phone and medium (including iPad landscape at 1180) stay two per row.
 */
const SPACE_DESKTOP_MIN_WIDTH = 1280;
/** Match Universal hub medium hero copy inset below desktop width. */
const MID_HERO_EDGE_INSET = 16;
/** Compact grid: never shrink below this; forces wrap to 2-up rows. */
const COMPACT_DETAIL_CARD_MIN_WIDTH = 148;
/** Space detail card title on phone (2-up grid). */
const DETAIL_CARD_TITLE_SIZE_PHONE = 13;
/** Large desktop: only a full 4-up row stretches to the content width. */
const DESKTOP_FILL_ROW_CARD_COUNT = 4;

/** Artboard ratios — detail frame 1440×864. Sizes below are exact on that frame. */
const FIGMA = {
  width: 1440,
  height: 864,
  /** Hero starts this far from the top of the frame. */
  heroTop: 95,
  heroHeight: 350,
  cardWidthExact: 299.833251953125,
  cardHeightExact: 329.8165588378906,
  /** Card row parent on the 1440×864 frame. */
  cardsRowWidth: 1376,
  cardsRowLeft: 32,
  cardsRowTop: 482,
  /** Spacing/4 */
  cardRadiusExact: 16,
  padSide: 72,
  padTop: 28,
  padBottom: 36,
  headerGap: 20,
  callButton: 48,
  copyMaxWidth: 760,
  /** Large — Typography `display` */
  titleSize: 64,
  /** Phone — Typography `largeTitle` (Font/Size/3XL) */
  titleSizePhone: 34,
  /**
   * Stacked hero (below desktop). Stays under the large-frame title (40)
   * and shrinks on short windows.
   */
  titleSizeMidMin: 28,
  titleSizeMidMax: 36,
  /** Large — Font/Size/2XL Medium */
  bodySize: 28,
  bodyLineHeight: 36,
  /** Phone — Font/Size/Base Regular · LH 22 */
  bodySizePhone: 16,
  bodyLineHeightPhone: 22,
  /** Stacked-hero body. Floor is the short-window size; ceiling is wide and tall. */
  bodySizeMidMin: 15,
  bodySizeMidMax: 18,
  bodyLineHeightMidMin: 22,
  bodyLineHeightMidMax: 26,
  gapTitleBody: 20,
  gapTitleBodyPhone: 16,
  gapBodyCta: 28,
  gapBodyCtaPhone: 20,
  ctaWidth: 277,
  ctaHeight: 48,
  ctaRadius: 4,
  ctaPadding: 12,
  ctaWidthCompact: 131,
  ctaHeightCompact: 44,
  ctaWidthMidMax: 220,
  ctaPaddingYCompact: 8,
  ctaPaddingXCompact: 12,
  ctaGap: 4,
  cardGap: 16,
  cardGapPhone: 12,
  cardWidth: 200,
  cardHeight: 210,
  cardRadius: 18,
} as const;

/**
 * Hero scrim over the photo and title.
 * Do not put backgroundImage in StyleSheet.create — RN strips it.
 */
const HERO_RADIAL_WEB: ViewStyle =
  Platform.OS === 'web'
    ? ({
      backgroundImage:
        `radial-gradient(50% 50% at 50% 50%, ${bloomColors.bloomUniversalHeroScrimCenter} 0%, ${bloomColors.bloomUniversalHeroScrimEdge} 100%)`,
      backgroundColor: 'transparent',
    } as ViewStyle)
    : {};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function lerp(from: number, to: number, t: number): number {
  return Math.round(from + (to - from) * t);
}

/** Horizontal inset for phone / tablet / sub-Figma desktop (header, hero, card grid). */
function resolveDetailPadSide(
  width: number,
  scaleX: number,
  isPhone: boolean,
  isTablet: boolean,
  midTypeT: number,
): number {
  if (isPhone) {
    return 16;
  }
  if (isTablet) {
    return lerp(16, 28, midTypeT);
  }
  if (width < FIGMA.width) {
    return lerp(
      28,
      FIGMA.cardsRowLeft,
      clamp(
        (width - SPACE_DESKTOP_MIN_WIDTH) / (FIGMA.width - SPACE_DESKTOP_MIN_WIDTH),
        0,
        1,
      ),
    );
  }
  return Math.round(FIGMA.padSide * clamp(scaleX, 0.55, 1.1));
}

const STACKED_HERO_TYPE_MIN_HEIGHT = 640;
const STACKED_HERO_TYPE_MAX_HEIGHT = 900;

/**
 * Type scale for the stacked hero. Width and height both count, and the
 * shorter axis wins so a wide but short window does not get desktop type.
 */
function resolveStackedHeroTypeT(width: number, height: number): number {
  const widthT = clamp(
    (width - PHONE_MAX) / (SPACE_DESKTOP_MIN_WIDTH - PHONE_MAX),
    0,
    1,
  );
  const heightT = clamp(
    (height - STACKED_HERO_TYPE_MIN_HEIGHT) /
      (STACKED_HERO_TYPE_MAX_HEIGHT - STACKED_HERO_TYPE_MIN_HEIGHT),
    0,
    1,
  );
  return Math.min(widthT, heightT);
}

function resolveMidTypeT(
  width: number,
  isPhone: boolean,
  isTablet: boolean,
  useTwoColumnGrid: boolean,
): number {
  if (isTablet) {
    return clamp(
      (width - PHONE_MAX) / (SPACE_DESKTOP_MIN_WIDTH - PHONE_MAX),
      0,
      1,
    );
  }
  if (useTwoColumnGrid && !isPhone) {
    return 1;
  }
  return 0;
}

function stackedHeroTitleTextAlign(isRTL: boolean): TextStyle {
  return { textAlign: isRTL ? ('right' as const) : ('left' as const) };
}

type SpaceLayout = {
  readonly padSide: number;
  readonly padTop: number;
  readonly padBottom: number;
  readonly callSize: number;
  readonly wordmarkScale: number;
  readonly copyMaxWidth: number;
  readonly titleVariant: 'largeTitle' | 'display';
  readonly titleFontSize: number | undefined;
  readonly bodyUsesBaseVariant: boolean;
  readonly bodyUsesMedium: boolean;
  readonly bodySize: number;
  readonly bodyLineHeight: number;
  readonly gapTitleBody: number;
  readonly gapBodyCta: number;
  readonly ctaWidth: number;
  readonly ctaHeight: number;
  readonly ctaRadius: number;
  readonly ctaPaddingVertical: number;
  readonly ctaPaddingHorizontal: number;
  readonly ctaGap: number;
  readonly cardGap: number;
  readonly cardWidth: number;
  readonly cardHeight: number;
  readonly cardRadius: number;
  readonly viewportHeight: number;
  readonly viewportWidth: number;
  readonly useTwoColumnGrid: boolean;
};

function resolveSpaceLayout(
  width: number,
  height: number,
  isPhone: boolean,
  isTablet: boolean,
): SpaceLayout {
  const scaleX = width / FIGMA.width;
  const typeScale = clamp(scaleX, 0.72, 1.05);
  /** 2-up grid below desktop width; 4-across Figma row from 1280. */
  const useTwoColumnGrid = width < SPACE_DESKTOP_MIN_WIDTH;
  const midTypeT = resolveMidTypeT(width, isPhone, isTablet, useTwoColumnGrid);

  const padSide = resolveDetailPadSide(
    width,
    scaleX,
    isPhone,
    isTablet,
    midTypeT,
  );

  const cardWidth = useTwoColumnGrid
    ? 0
    : Math.max(FIGMA.cardWidth, Math.round(FIGMA.cardWidth * typeScale));
  const cardHeight = useTwoColumnGrid
    ? FIGMA.cardHeight
    : Math.max(FIGMA.cardHeight, Math.round(FIGMA.cardHeight * typeScale));

  if (isPhone) {
    return {
      padSide,
      padTop: 16,
      padBottom: 20,
      callSize: 44,
      wordmarkScale: 0.42,
      copyMaxWidth: width - 40,
      titleVariant: 'largeTitle',
      titleFontSize: undefined,
      bodyUsesBaseVariant: true,
      bodyUsesMedium: false,
      bodySize: FIGMA.bodySizePhone,
      bodyLineHeight: FIGMA.bodyLineHeightPhone,
      gapTitleBody: FIGMA.gapTitleBodyPhone,
      gapBodyCta: FIGMA.gapBodyCtaPhone,
      ctaWidth: FIGMA.ctaWidthCompact,
      ctaHeight: FIGMA.ctaHeightCompact,
      ctaRadius: FIGMA.ctaRadius,
      ctaPaddingVertical: FIGMA.ctaPaddingYCompact,
      ctaPaddingHorizontal: FIGMA.ctaPaddingXCompact,
      ctaGap: FIGMA.ctaGap,
      cardGap: FIGMA.cardGapPhone,
      cardWidth,
      cardHeight,
      cardRadius: FIGMA.cardRadius,
      viewportHeight: height,
      viewportWidth: width,
      useTwoColumnGrid: true,
    };
  }

  if (useTwoColumnGrid && !isPhone) {
    const heroTypeT = resolveStackedHeroTypeT(width, height);
    return {
      padSide,
      padTop: Math.round(FIGMA.padTop * typeScale),
      padBottom: Math.round(FIGMA.padBottom * typeScale),
      callSize: Math.round(FIGMA.callButton * typeScale),
      wordmarkScale: clamp(scaleX * 0.5, 0.4, 0.55),
      /** Full hero width so the description is not capped at the desktop copy column. */
      copyMaxWidth: width - MID_HERO_EDGE_INSET * 2,
      titleVariant: 'largeTitle',
      titleFontSize: lerp(
        FIGMA.titleSizeMidMin,
        FIGMA.titleSizeMidMax,
        heroTypeT,
      ),
      bodyUsesBaseVariant: false,
      bodyUsesMedium: false,
      bodySize: lerp(FIGMA.bodySizeMidMin, FIGMA.bodySizeMidMax, heroTypeT),
      bodyLineHeight: lerp(
        FIGMA.bodyLineHeightMidMin,
        FIGMA.bodyLineHeightMidMax,
        heroTypeT,
      ),
      gapTitleBody: lerp(FIGMA.gapTitleBodyPhone, FIGMA.gapTitleBody, midTypeT),
      gapBodyCta: lerp(FIGMA.gapBodyCtaPhone, FIGMA.gapBodyCta, midTypeT),
      ctaWidth: lerp(FIGMA.ctaWidthCompact, FIGMA.ctaWidthMidMax, midTypeT),
      ctaHeight: lerp(FIGMA.ctaHeightCompact, FIGMA.ctaHeight, midTypeT),
      ctaRadius: FIGMA.ctaRadius,
      ctaPaddingVertical: lerp(
        FIGMA.ctaPaddingYCompact,
        FIGMA.ctaPadding,
        midTypeT,
      ),
      ctaPaddingHorizontal: lerp(
        FIGMA.ctaPaddingXCompact,
        FIGMA.ctaPadding,
        midTypeT,
      ),
      ctaGap: FIGMA.ctaGap,
      cardGap: lerp(FIGMA.cardGapPhone, FIGMA.cardGap, midTypeT),
      cardWidth,
      cardHeight,
      cardRadius: FIGMA.cardRadius,
      viewportHeight: height,
      viewportWidth: width,
      useTwoColumnGrid: true,
    };
  }

  /** Large desktop (≥1440) — Figma artboard; do not change. */
  return {
    padSide,
    padTop: Math.round(FIGMA.padTop * typeScale),
    padBottom: Math.round(FIGMA.padBottom * typeScale),
    callSize: Math.round(FIGMA.callButton * typeScale),
    wordmarkScale: clamp(scaleX * 0.5, 0.4, 0.55),
    copyMaxWidth: Math.max(
      FIGMA.copyMaxWidth,
      Math.round(FIGMA.copyMaxWidth * clamp(scaleX, 0.85, 1.15)),
    ),
    titleVariant: 'display',
    titleFontSize: undefined,
    bodyUsesBaseVariant: false,
    bodyUsesMedium: true,
    bodySize: Math.round(FIGMA.bodySize * typeScale),
    bodyLineHeight: Math.round(FIGMA.bodyLineHeight * typeScale),
    gapTitleBody: Math.round(FIGMA.gapTitleBody * typeScale),
    gapBodyCta: Math.round(FIGMA.gapBodyCta * typeScale),
    ctaWidth: Math.round(FIGMA.ctaWidth * typeScale),
    ctaHeight: Math.round(FIGMA.ctaHeight * typeScale),
    ctaRadius: FIGMA.ctaRadius,
    ctaPaddingVertical: Math.round(FIGMA.ctaPadding * typeScale),
    ctaPaddingHorizontal: Math.round(FIGMA.ctaPadding * typeScale),
    ctaGap: FIGMA.ctaGap,
    cardGap: Math.round(FIGMA.cardGap * clamp(scaleX, 0.75, 1)),
    cardWidth,
    cardHeight,
    cardRadius: FIGMA.cardRadius,
    viewportHeight: height,
    viewportWidth: width,
    useTwoColumnGrid: false,
  };
}

function DetailCard({
  card,
  width,
  height,
  gutter,
  borderRadius,
  titleFontSize,
  subtitleFontSize,
  onCardPress,
}: Readonly<{
  card: BloomUniversalSpaceCardContent;
  width: DimensionValue;
  height: number;
  gutter: number;
  borderRadius: number;
  titleFontSize?: number;
  subtitleFontSize?: number;
  onCardPress?: () => void;
}>) {
  const href = card.cardHref?.trim() || '';

  const handlePress = useCallback(() => {
    if (!href) {
      return;
    }
    onCardPress?.();
    if (href.startsWith('/')) {
      router.push(href as never);
      return;
    }
    void Linking.openURL(href);
  }, [href, onCardPress]);

  return (
    <BloomSpaceCard
      variant="detail"
      title={card.title}
      {...(card.subtitle ? { subtitle: card.subtitle } : {})}
      {...(card.imageUrl ? { tileImageUrl: card.imageUrl } : {})}
      {...(href ? { onPress: handlePress } : {})}
      width={width}
      height={height}
      gutter={gutter}
      borderRadius={borderRadius}
      {...(titleFontSize != null ? { titleFontSize } : {})}
      {...(subtitleFontSize != null ? { subtitleFontSize } : {})}
    />
  );
}

function SpaceCallButton({
  callSize,
  isRTL,
  phoneNumber,
  phoneIconUrl,
}: Readonly<{
  callSize: number;
  isRTL: boolean;
  phoneNumber?: string;
  phoneIconUrl?: string;
}>) {
  const { t } = useTranslation();
  const phoneIconSize = Math.round(callSize * 0.42);
  const cmsPhone = phoneNumber?.trim() || '';
  const displayPhone = cmsPhone
    ? formatPhoneNumberDisplay(cmsPhone) || cmsPhone
    : '';
  const [showInfo, setShowInfo] = useState(false);

  const handleCall = () => {
    if (!cmsPhone) {
      return;
    }
    openPhoneDialer(cmsPhone);
  };

  return (
    <View style={styles.callButtonWrap}>
      <Pressable
        onPress={handleCall}
        onHoverIn={() => {
          if (cmsPhone) {
            setShowInfo(true);
          }
        }}
        onHoverOut={() => setShowInfo(false)}
        disabled={!cmsPhone}
        accessibilityRole="button"
        accessibilityLabel={
          cmsPhone
            ? `${t('bloomUniversal.callSupport')}: ${displayPhone}`
            : t('bloomUniversal.callSupport')
        }
        style={({ pressed }) => [
          styles.callButton,
          {
            width: callSize,
            height: callSize,
            borderRadius: Math.round(callSize * 0.22),
            borderWidth: 1,
            borderColor: bloomColors.bloomUniversalMuted,
            boxShadow: `0px 0px 19px 0px ${bloomColors.bloomUniversalCallShadow}`,
          } as ViewStyle,
          pressed && cmsPhone ? styles.callButtonPressed : null,
        ]}
      >
        <BloomCmsIcon
          url={phoneIconUrl}
          size={phoneIconSize}
          fallback={
            <BloomSpacePhoneIcon size={phoneIconSize} color={bloomColors.bloomUniversalInkStrong} />
          }
        />
      </Pressable>

      {Platform.OS === 'web' && showInfo && displayPhone ? (
        <View
          style={[
            styles.callInfoTip,
            isRTL ? styles.callInfoTipRtl : null,
            {
              boxShadow: `0 6px 16px ${bloomColors.bloomUniversalCallLiftShadow}`,
            } as ViewStyle,
          ]}
          pointerEvents="none"
        >
          <View
            style={[
              styles.callInfoCaret,
              isRTL ? styles.callInfoCaretRtl : null,
            ]}
          />
          <Typography
            variant="footnote"
            color="secondary"
            style={styles.callInfoLabel}
          >
            {t('bloomUniversal.call')}
          </Typography>
          <Typography
            variant="callout"
            color="primary"
            style={[styles.callInfoNumber, styles.callInfoNumberLtr]}
          >
            {displayPhone}
          </Typography>
        </View>
      ) : null}
    </View>
  );
}

function SpacePageHeader({
  spaceTitle,
  isRTL,
  callSize,
  wordmarkScale,
  phoneNumber,
  phoneIconUrl,
  breadcrumbChevronIconUrl,
  isCompact,
  currentTypeSize,
}: Readonly<{
  spaceTitle: string;
  isRTL: boolean;
  callSize: number;
  wordmarkScale: number;
  phoneNumber?: string;
  phoneIconUrl?: string;
  breadcrumbChevronIconUrl?: string;
  /** Phone: breadcrumb on its own row so it doesn’t wrap under the logo. */
  isCompact: boolean;
  /** Scaled Font/Size/L for the current crumb. */
  currentTypeSize?: number;
}>) {
  const { t } = useTranslation();
  const crumbType =
    currentTypeSize != null
      ? {
        fontFamily: 'Matter-Medium' as const,
        fontWeight: '400' as const,
        fontSize: currentTypeSize,
        lineHeight: currentTypeSize,
        letterSpacing: 0,
        textAlign: 'center' as const,
        color: bloomColors.bloomUniversalInk,
      }
      : null;

  const breadcrumb = (
    <View
      style={[
        styles.breadcrumb,
        isCompact ? styles.breadcrumbCompact : null,
      ]}
    >
      <Pressable
        onPress={() => router.replace(BLOOM_UNIVERSAL_HUB_PATH as never)}
        accessibilityRole="link"
        accessibilityLabel={t('bloomUniversal.breadcrumbHome')}
        style={styles.breadcrumbHomeHit}
      >
        <Typography
          variant="callout"
          color="primary"
          style={[styles.breadcrumbHome, crumbType]}
        >
          {t('bloomUniversal.breadcrumbHome')}
        </Typography>
      </Pressable>
      <View style={isRTL ? styles.breadcrumbChevronRtl : null}>
        <BloomCmsIcon
          url={breadcrumbChevronIconUrl}
          size={11}
          fallback={
            <BloomBreadcrumbChevronIcon size={11} color={bloomColors.bloomUniversalInk} />
          }
        />
      </View>
      <Typography
        variant="callout"
        color="primary"
        numberOfLines={1}
        ellipsizeMode="tail"
        style={[styles.breadcrumbCurrent, crumbType]}
      >
        {spaceTitle}
      </Typography>
    </View>
  );

  const trailing = (
    <View
      style={styles.headerTrailing}
    >
      <LanguageToggle color="fontPrimary" />
      <SpaceCallButton
        callSize={callSize}
        isRTL={isRTL}
        {...(phoneNumber ? { phoneNumber } : {})}
        {...(phoneIconUrl ? { phoneIconUrl } : {})}
      />
    </View>
  );

  if (isCompact) {
    return (
      <View style={styles.headerBlock}>
        <View style={styles.header}>
          <Pressable
            onPress={() => router.replace(BLOOM_UNIVERSAL_HUB_PATH as never)}
            accessibilityRole="link"
            accessibilityLabel="Bloom"
            style={styles.headerWordmarkHit}
          >
            <BloomWordmark scale={wordmarkScale} />
          </Pressable>
          {trailing}
        </View>
        {breadcrumb}
      </View>
    );
  }

  return (
    <View style={styles.header}>
      <View style={styles.headerLeading}>
        <Pressable
          onPress={() => router.replace(BLOOM_UNIVERSAL_HUB_PATH as never)}
          accessibilityRole="link"
          accessibilityLabel="Bloom"
          style={styles.headerWordmarkHit}
        >
          <BloomWordmark scale={wordmarkScale} />
        </Pressable>
        {breadcrumb}
      </View>
      {trailing}
    </View>
  );
}

/** One slot in the desktop 4-column grid — same width on every row when cards wrap. */
function desktopFourColumnSlotStyle(cardGap: number): ViewStyle {
  const gapTotal = (DESKTOP_FILL_ROW_CARD_COUNT - 1) * cardGap;
  const columns = DESKTOP_FILL_ROW_CARD_COUNT;
  if (Platform.OS === 'web') {
    const slot = `calc((100% - ${gapTotal}px) / ${columns})`;
    return {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: slot,
      width: slot,
      maxWidth: slot,
      minWidth: 0,
    } as ViewStyle;
  }
  return {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: `${100 / columns}%`,
    width: `${100 / columns}%`,
    maxWidth: `${100 / columns}%`,
    minWidth: 0,
  };
}

function SpaceDetailCards({
  cards,
  useTwoColumnGrid,
  cardWidth,
  cardHeight,
  cardGap,
  cardRadius,
  titleFontSize,
  subtitleFontSize,
  isRTL,
  onCardPress,
}: Readonly<{
  cards: readonly BloomUniversalSpaceCardContent[];
  useTwoColumnGrid: boolean;
  cardWidth: number;
  cardHeight: number;
  cardGap: number;
  cardRadius: number;
  titleFontSize?: number;
  subtitleFontSize?: number;
  isRTL: boolean;
  onCardPress?: () => void;
}>) {
  if (cards.length === 0) {
    return null;
  }

  if (!useTwoColumnGrid) {
    const rowDirection = isRTL ? ('row-reverse' as const) : ('row' as const);
    const renderDetailCard = (card: BloomUniversalSpaceCardContent) => (
      <DetailCard
        card={card}
        width="100%"
        height={cardHeight}
        gutter={0}
        borderRadius={cardRadius}
        {...(titleFontSize != null ? { titleFontSize } : {})}
        {...(subtitleFontSize != null ? { subtitleFontSize } : {})}
        {...(onCardPress ? { onCardPress } : {})}
      />
    );

    /** 4+ cards: 4-column grid with wrap — every card same width/height as a 4-up row. */
    if (cards.length >= DESKTOP_FILL_ROW_CARD_COUNT) {
      const slotStyle = desktopFourColumnSlotStyle(cardGap);
      return (
        <View
          style={[
            styles.cardsRow,
            {
              width: '100%',
              justifyContent: 'flex-start',
              flexDirection: rowDirection,
              gap: cardGap,
            },
          ]}
        >
          {cards.map((card) => (
            <View key={card.cardKey} style={slotStyle}>
              {renderDetailCard(card)}
            </View>
          ))}
        </View>
      );
    }

    /** 1–3 cards: Figma-scaled fixed width, no stretch. */
    return (
      <View
        style={[
          styles.cardsRow,
          {
            width: '100%',
            minHeight: cardHeight,
            justifyContent: 'flex-start',
            flexDirection: rowDirection,
            gap: cardGap,
          },
        ]}
      >
        {cards.map((card) => (
          <View
            key={card.cardKey}
            style={[styles.equalCardSlotFixed, { width: cardWidth }]}
          >
            {renderDetailCard(card)}
          </View>
        ))}
      </View>
    );
  }

  /** 2-up pairs; last odd card spans full width. Equal side inset via flex + gap (no negative margin). */
  return (
    <View style={[styles.cardsRow, styles.cardsRowCompact, { gap: cardGap }]}>
      {cards.map((card, index) => {
        const isLastAlone =
          cards.length % 2 === 1 && index === cards.length - 1;
        return (
          <View
            key={card.cardKey}
            style={[
              styles.compactCardSlot,
              isLastAlone
                ? styles.compactCardFull
                : [
                    styles.compactCardPair,
                    {
                      minWidth: COMPACT_DETAIL_CARD_MIN_WIDTH,
                      ...(Platform.OS === 'web'
                        ? ({
                            flexBasis: `calc((100% - ${cardGap}px) / 2)`,
                            maxWidth: `calc((100% - ${cardGap}px) / 2)`,
                            width: `calc((100% - ${cardGap}px) / 2)`,
                          } as object)
                        : {
                            width: '48%',
                            maxWidth: '48%',
                          }),
                    },
                  ],
            ]}
          >
            <DetailCard
              card={card}
              width="100%"
              height={cardHeight}
              gutter={0}
              borderRadius={cardRadius}
              {...(titleFontSize != null ? { titleFontSize } : {})}
              {...(subtitleFontSize != null ? { subtitleFontSize } : {})}
              {...(onCardPress ? { onCardPress } : {})}
            />
          </View>
        );
      })}
    </View>
  );
}

/**
 * Space detail page — full-viewport hero, header, copy + CTA, detail cards.
 * Same shell for every space; content from Contentful-shaped fields.
 */
function readRouteSpaceId(id: string | string[] | undefined): {
  rawId: string | undefined;
  spaceKey: string;
} {
  const rawId = Array.isArray(id) ? id[0] : id;
  return { rawId, spaceKey: rawId?.trim() ?? '' };
}

function trimCmsField(value: string | undefined): string {
  return value?.trim() ?? '';
}

function resolvePageCopy(
  space: BloomUniversalSpaceContent | undefined,
  comingSoon: string,
  discoverMore: string,
): {
  breadcrumbTitle: string;
  title: string;
  body: string;
  ctaLabel: string;
  pageCards: readonly BloomUniversalSpaceCardContent[];
} {
  const breadcrumbTitle = trimCmsField(space?.title);
  const headline = trimCmsField(space?.pageHeadline);
  const title = headline || breadcrumbTitle || comingSoon;
  const body = trimCmsField(space?.pageBody) || comingSoon;
  const ctaLabel = trimCmsField(space?.pageCtaLabel) || discoverMore;
  return {
    breadcrumbTitle,
    title,
    body,
    ctaLabel,
    pageCards: space?.pageCards ?? [],
  };
}

type DetailFrame = {
  readonly isLargeFrame: boolean;
  readonly frameScaleX: number;
  readonly frameScaleY: number;
  readonly cardsGap: number;
  readonly detailCardHeight: number;
  readonly detailCardWidth: number;
  readonly detailCardRadius: number;
  readonly detailCardGap: number;
  readonly detailPadSide: number;
  readonly headerBandHeight: number | undefined;
  readonly heroBandHeight: number | undefined;
  readonly cardsRowInset: number;
};

function resolveDetailFrame(layout: SpaceLayout): DetailFrame {
  const isLargeFrame = !layout.useTwoColumnGrid;
  const frameScaleX = layout.viewportWidth / FIGMA.width;
  const frameScaleY = layout.viewportHeight / FIGMA.height;
  if (!isLargeFrame) {
    return {
      isLargeFrame,
      frameScaleX,
      frameScaleY,
      cardsGap: 0,
      detailCardHeight: layout.cardHeight,
      detailCardWidth: layout.cardWidth,
      detailCardRadius: layout.cardRadius,
      detailCardGap: layout.cardGap,
      detailPadSide: layout.padSide,
      headerBandHeight: undefined,
      heroBandHeight: undefined,
      cardsRowInset: layout.padSide,
    };
  }
  const largeCardGap =
    ((FIGMA.width - FIGMA.padSide * 2 - FIGMA.cardWidthExact * 4) / 3) *
    frameScaleX;
  return {
    isLargeFrame,
    frameScaleX,
    frameScaleY,
    cardsGap:
      (FIGMA.cardsRowTop - FIGMA.heroTop - FIGMA.heroHeight) * frameScaleY,
    detailCardHeight: (FIGMA.cardHeightExact + 36) * frameScaleY,
    detailCardWidth: FIGMA.cardWidthExact * frameScaleX,
    detailCardRadius: FIGMA.cardRadiusExact * frameScaleY,
    detailCardGap: largeCardGap,
    detailPadSide: FIGMA.padSide * frameScaleX,
    headerBandHeight: FIGMA.heroTop * frameScaleY,
    heroBandHeight: FIGMA.heroHeight * frameScaleY,
    cardsRowInset: FIGMA.cardsRowLeft * frameScaleX,
  };
}

function headerBarStyle(
  frame: DetailFrame,
  layout: SpaceLayout,
): ViewStyle {
  const { headerBandHeight } = frame;
  return {
    height: headerBandHeight,
    paddingTop: headerBandHeight == null ? layout.padTop : 0,
    paddingLeft: frame.isLargeFrame ? frame.cardsRowInset : frame.detailPadSide,
    paddingRight: frame.isLargeFrame ? frame.cardsRowInset : frame.detailPadSide,
    justifyContent: headerBandHeight == null ? undefined : 'center',
  };
}

function heroBandStyle(frame: DetailFrame): ViewStyle | null {
  if (frame.heroBandHeight == null) {
    return null;
  }
  return {
    height: frame.heroBandHeight,
    flexGrow: 0,
    flexShrink: 0,
    minHeight: frame.heroBandHeight,
  };
}

function heroCopyBandStyle(
  frame: DetailFrame,
  isRTL: boolean,
  isPhone: boolean,
  useStackedHero: boolean,
  useHeroOverlayCenter: boolean,
): ViewStyle {
  if (useHeroOverlayCenter) {
    return {
      paddingHorizontal: MID_HERO_EDGE_INSET,
    };
  }
  if (useStackedHero && isPhone) {
    return {
      paddingHorizontal: MID_HERO_EDGE_INSET,
      paddingVertical: 28,
    };
  }
  if (!frame.isLargeFrame) {
    return {
      paddingHorizontal: frame.detailPadSide,
      paddingTop: isPhone ? 24 : 48,
      paddingBottom: isPhone ? 28 : 56,
    };
  }
  return {
    height: '100%',
    justifyContent: 'center',
    alignItems: isRTL ? 'flex-end' : 'flex-start',
    paddingHorizontal: frame.cardsRowInset,
  };
}

function largeTitleStyle(
  frame: DetailFrame,
  layout: SpaceLayout,
  isRTL: boolean,
): TextStyle {
  if (!frame.isLargeFrame) {
    return {
      marginBottom: layout.gapTitleBody,
      maxWidth: layout.viewportWidth - frame.detailPadSide * 2,
      flexShrink: 0,
    };
  }
  return {
    fontFamily: 'Matter-SemiBold',
    fontWeight: '400',
    fontSize: 40 * frame.frameScaleY,
    lineHeight: 40 * frame.frameScaleY,
    letterSpacing: 0,
    textAlign: isRTL ? 'right' : 'left',
    alignSelf: isRTL ? 'flex-end' : 'flex-start',
    width: '100%',
    marginBottom: 20 * frame.frameScaleY,
    maxWidth: layout.viewportWidth - frame.detailPadSide * 2,
    flexShrink: 0,
  };
}

function largeBodyStyle(
  frame: DetailFrame,
  layout: SpaceLayout,
  isRTL: boolean,
): TextStyle {
  if (!frame.isLargeFrame) {
    return { marginBottom: layout.gapBodyCta };
  }
  return {
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 22 * frame.frameScaleY,
    lineHeight: 25 * frame.frameScaleY,
    letterSpacing: 0,
    textAlign: isRTL ? 'right' : 'left',
    marginBottom: 56 * frame.frameScaleY,
  };
}

function ctaTextStyle(frame: DetailFrame): TextStyle | null {
  if (!frame.isLargeFrame) {
    return null;
  }
  return {
    fontFamily: 'Matter-SemiBold',
    fontWeight: '400',
    fontSize: 16 * frame.frameScaleY,
    lineHeight: 16 * frame.frameScaleY,
    letterSpacing: 0,
    textAlign: 'center',
  };
}

function cardLabelSizes(
  frame: DetailFrame,
  isPhone: boolean,
): {
  titleFontSize: number;
  subtitleFontSize: number;
} {
  if (!frame.isLargeFrame) {
    return {
      titleFontSize: isPhone ? DETAIL_CARD_TITLE_SIZE_PHONE : 20,
      subtitleFontSize: 15,
    };
  }
  return {
    titleFontSize: 20 * frame.frameScaleY,
    subtitleFontSize: 15 * frame.frameScaleY,
  };
}

function ctaButtonStyle(layout: SpaceLayout, isRTL: boolean): ViewStyle {
  return {
    width: layout.ctaWidth,
    height: layout.ctaHeight,
    borderRadius: layout.ctaRadius,
    paddingTop: layout.ctaPaddingVertical,
    paddingBottom: layout.ctaPaddingVertical,
    paddingLeft: layout.ctaPaddingHorizontal,
    paddingRight: layout.ctaPaddingHorizontal,
    gap: layout.ctaGap,
    alignSelf: isRTL ? 'flex-end' : 'flex-start',
  };
}

export function BloomUniversalSpaceScreen() {
  const { t, currentLanguage } = useTranslation();
  const { isRTL } = useI18nContext();
  // breakpointWidth/Height are zoom-corrected: using raw useWindowDimensions()
  // here causes minHeight values to get CSS-zoomed to 80% at 125% browser zoom,
  // leaving a white gap at the bottom identical to the 100dvh problem.
  const { breakpointWidth: width, breakpointHeight: height } = useWebViewport();
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const { rawId, spaceKey } = readRouteSpaceId(params.id);

  // Properties no longer has a Universal space detail page — use guest catalogue.
  useEffect(() => {
    if (spaceKey === BLOOM_UNIVERSAL_PROPERTIES_SPACE_KEY) {
      router.replace(BLOOM_UNIVERSAL_PROPERTIES_GUEST_PATH as never);
    }
  }, [spaceKey]);

  const { hub, space, isLoading } = useBloomUniversalSpaceContent(
    spaceKey === BLOOM_UNIVERSAL_PROPERTIES_SPACE_KEY ? undefined : rawId,
  );

  // Inactive / missing CMS space → back to hub (no fallback page content).
  useEffect(() => {
    if (spaceKey === BLOOM_UNIVERSAL_PROPERTIES_SPACE_KEY) {
      return;
    }
    if (!spaceKey || isLoading) {
      return;
    }
    if (!space) {
      router.replace(BLOOM_UNIVERSAL_HUB_PATH as never);
    }
  }, [spaceKey, space, isLoading]);

  const isPhone = width < PHONE_MAX;
  const isTablet =
    width >= PHONE_MAX && width < SPACE_DESKTOP_MIN_WIDTH;

  const layout = useMemo(
    () => resolveSpaceLayout(width, height, isPhone, isTablet),
    [width, height, isPhone, isTablet],
  );

  const pageCopy = resolvePageCopy(
    space,
    t('bloomUniversal.spaceComingSoon'),
    t('bloomUniversal.discoverMore'),
  );
  const frame = resolveDetailFrame(layout);

  const heroUrl = useMemo(
    () =>
      resolveBloomUniversalImageUrl(
        space?.pageHeroImageUrl ??
        space?.tileImageUrl ??
        hub?.heroBackgroundUrl,
      ),
    [space?.pageHeroImageUrl, space?.tileImageUrl, hub?.heroBackgroundUrl],
  );

  const spaceDetailProps = useMemo(() => {
    if (!space || !spaceKey) {
      return undefined;
    }
    return landingSpaceDetailProperties({
      spaceKey,
      spaceName: space.title,
      experienceName: space.pageHeadline || space.title,
      locale: currentLanguage,
    });
  }, [space, spaceKey, currentLanguage]);

  useCatalogDynatraceView(
    LANDING_PAGE_SCREEN.SPACE,
    LANDING_PAGE_ACTION.SPACE_VIEWED,
    Boolean(space && spaceKey && !isLoading),
    spaceDetailProps,
  );

  const handleInstitutionCard = useCallback(() => {
    if (!spaceDetailProps) {
      return;
    }
    trackCatalogEvent({
      screen: LANDING_PAGE_SCREEN.SPACE,
      actionName: LANDING_PAGE_ACTION.SPACE_INSTITUTION_CARD_CLICK,
      eventType: 'action',
      properties: spaceDetailProps,
    });
  }, [spaceDetailProps]);

  const handleCta = useCallback(() => {
    if (spaceDetailProps) {
      trackCatalogEvent({
        screen: LANDING_PAGE_SCREEN.SPACE,
        actionName: LANDING_PAGE_ACTION.SPACE_DISCOVER_MORE_CLICK,
        eventType: 'action',
        properties: spaceDetailProps,
      });
    }
    const href = space?.pageCtaHref?.trim();
    if (!href) {
      return;
    }
    if (href.startsWith('/')) {
      router.push(href as never);
      return;
    }
    void Linking.openURL(href);
  }, [space?.pageCtaHref, spaceDetailProps]);

  const titleTypeStyle = useMemo(() => {
    if (layout.titleVariant === 'display') {
      return { lineHeight: FIGMA.titleSize };
    }
    if (layout.titleFontSize != null) {
      return {
        fontSize: layout.titleFontSize,
        lineHeight: layout.titleFontSize,
      };
    }
    return null;
  }, [layout.titleVariant, layout.titleFontSize]);

  const bodyTypeStyle = useMemo(() => {
    if (layout.bodyUsesBaseVariant) {
      return { lineHeight: layout.bodyLineHeight };
    }
    return {
      fontSize: layout.bodySize,
      lineHeight: layout.bodyLineHeight,
    };
  }, [
    layout.bodyUsesBaseVariant,
    layout.bodySize,
    layout.bodyLineHeight,
  ]);

  const showSpaceDocumentTitle =
    Platform.OS === 'web' &&
    spaceKey !== BLOOM_UNIVERSAL_PROPERTIES_SPACE_KEY &&
    !isLoading &&
    Boolean(hub && space);
  useWebDocumentTitle(
    showSpaceDocumentTitle ? pageCopy.title : undefined,
    showSpaceDocumentTitle ? pageCopy.body : undefined,
  );

  if (Platform.OS !== 'web') {
    return null;
  }

  if (spaceKey === BLOOM_UNIVERSAL_PROPERTIES_SPACE_KEY) {
    return null;
  }

  // Wait for Contentful — blank until response; no fallback flash while loading.
  if (isLoading || !hub || !space) {
    return null;
  }

  return (
    <SpaceDetailPage
      layout={layout}
      frame={frame}
      isRTL={isRTL}
      isPhone={isPhone}
      isTablet={isTablet}
      hub={hub}
      title={pageCopy.title}
      body={pageCopy.body}
      ctaLabel={pageCopy.ctaLabel}
      breadcrumbTitle={pageCopy.breadcrumbTitle}
      pageCards={pageCopy.pageCards}
      heroUrl={heroUrl}
      titleTypeStyle={titleTypeStyle}
      bodyTypeStyle={bodyTypeStyle}
      onCta={handleCta}
      onCardPress={handleInstitutionCard}
    />
  );
}

function SpaceDetailHeroCopy({
  layout,
  frame,
  isRTL,
  title,
  body,
  ctaLabel,
  titleTypeStyle,
  bodyTypeStyle,
  onCta,
  useStackedHero,
  heroOnPhoto,
}: Readonly<{
  layout: SpaceLayout;
  frame: DetailFrame;
  isRTL: boolean;
  title: string;
  body: string;
  ctaLabel: string;
  titleTypeStyle: TextStyle | null;
  bodyTypeStyle: TextStyle;
  onCta: () => void;
  useStackedHero: boolean;
  heroOnPhoto: boolean;
}>) {
  const bodyColor = heroOnPhoto
    ? bloomColors.bloomUniversalOnPhoto
    : bloomColors.bloomUniversalMuted;

  return (
    <>
      <Typography
        variant={layout.titleVariant}
        color="white"
        numberOfLines={layout.useTwoColumnGrid ? 3 : 1}
        ellipsizeMode="tail"
        style={[
          styles.pageTitle,
          titleTypeStyle,
          largeTitleStyle(frame, layout, isRTL),
          useStackedHero ? stackedHeroTitleTextAlign(isRTL) : null,
        ]}
      >
        {title}
      </Typography>
      <View
        style={[
          styles.heroCopy,
          { maxWidth: layout.copyMaxWidth },
          isRTL ? styles.heroCopyRtl : null,
        ]}
        {...(isRTL ? webViewDirProps('ltr') : {})}
      >
        <Typography
          variant="body"
          color={bodyColor}
          style={[
            styles.pageBody,
            layout.bodyUsesMedium ? styles.pageBodyLarge : null,
            bodyTypeStyle,
            largeBodyStyle(frame, layout, isRTL),
          ]}
        >
          {body}
        </Typography>
        <Pressable
          onPress={onCta}
          accessibilityRole="button"
          accessibilityLabel={ctaLabel}
          {...(isRTL ? webViewDirProps('ltr') : {})}
          style={({ pressed }) => [
            styles.cta,
            ctaButtonStyle(layout, isRTL),
            pressed ? styles.ctaPressed : null,
          ]}
        >
          <Typography
            variant="headline"
            color="primary"
            style={[styles.ctaText, ctaTextStyle(frame)]}
          >
            {ctaLabel}
          </Typography>
        </Pressable>
      </View>
    </>
  );
}

function SpaceDetailHeroBand({
  layout,
  frame,
  isRTL,
  isPhone,
  title,
  body,
  ctaLabel,
  heroUrl,
  titleTypeStyle,
  bodyTypeStyle,
  onCta,
  useStackedHero,
  useHeroOverlayCenter,
  heroOnPhoto,
}: Readonly<{
  layout: SpaceLayout;
  frame: DetailFrame;
  isRTL: boolean;
  isPhone: boolean;
  title: string;
  body: string;
  ctaLabel: string;
  heroUrl: string | undefined;
  titleTypeStyle: TextStyle | null;
  bodyTypeStyle: TextStyle;
  onCta: () => void;
  useStackedHero: boolean;
  useHeroOverlayCenter: boolean;
  heroOnPhoto: boolean;
}>) {
  return (
    <View
      style={[
        styles.heroBand,
        useHeroOverlayCenter && frame.heroBandHeight == null
          ? styles.heroBandMid
          : null,
        useStackedHero && isPhone ? styles.heroBandPhone : null,
        heroBandStyle(frame),
      ]}
    >
      {heroUrl ? (
        <Image
          source={{ uri: heroUrl }}
          style={styles.heroImage}
          resizeMode="cover"
          accessibilityIgnoresInvertColors
        />
      ) : (
        <View style={[styles.heroImage, styles.heroFallback]} />
      )}
      <View pointerEvents="none" style={[styles.pageScrim, HERO_RADIAL_WEB]} />

      <View
        style={[
          styles.heroCopyBand,
          useStackedHero && isPhone ? styles.heroCopyBandPhone : null,
          useHeroOverlayCenter ? styles.spaceHeroCopyOverlay : null,
          heroCopyBandStyle(
            frame,
            isRTL,
            isPhone,
            useStackedHero,
            useHeroOverlayCenter,
          ),
        ]}
        {...(isRTL && (useStackedHero || frame.isLargeFrame)
          ? webViewDirProps('ltr')
          : {})}
      >
        <View
          style={[
            useStackedHero ? styles.spaceHeroCopyInner : null,
            isRTL && useStackedHero ? styles.heroCopyRtl : null,
          ]}
          {...(isRTL && useStackedHero ? webViewDirProps('ltr') : {})}
        >
          <SpaceDetailHeroCopy
            layout={layout}
            frame={frame}
            isRTL={isRTL}
            title={title}
            body={body}
            ctaLabel={ctaLabel}
            titleTypeStyle={titleTypeStyle}
            bodyTypeStyle={bodyTypeStyle}
            onCta={onCta}
            useStackedHero={useStackedHero}
            heroOnPhoto={heroOnPhoto}
          />
        </View>
      </View>
    </View>
  );
}

/** Phone and desktop show one card row; medium shows up to two. */
function spaceVisibleCardRows(
  isLargeFrame: boolean,
  isPhone: boolean,
  cardRows: number,
): number {
  if (isLargeFrame || isPhone) {
    return 1;
  }
  return Math.min(2, cardRows);
}

function spaceCardsBandPadTop(isLargeFrame: boolean, isPhone: boolean): number {
  if (isLargeFrame) {
    return 0;
  }
  if (isPhone) {
    return 20;
  }
  return 28;
}

function SpaceDetailPage({
  layout,
  frame,
  isRTL,
  isPhone,
  hub,
  title,
  body,
  ctaLabel,
  breadcrumbTitle,
  pageCards,
  heroUrl,
  titleTypeStyle,
  bodyTypeStyle,
  onCta,
  onCardPress,
}: Readonly<{
  layout: SpaceLayout;
  frame: DetailFrame;
  isRTL: boolean;
  isPhone: boolean;
  isTablet: boolean;
  hub: BloomUniversalHubContent;
  title: string;
  body: string;
  ctaLabel: string;
  breadcrumbTitle: string;
  pageCards: readonly BloomUniversalSpaceCardContent[];
  heroUrl: string | undefined;
  titleTypeStyle: TextStyle | null;
  bodyTypeStyle: TextStyle;
  onCta: () => void;
  onCardPress?: () => void;
}>) {
  const labelSizes = cardLabelSizes(frame, isPhone);
  /** Below desktop width: hero on photo. At ≥1280 uses the large-frame hero band. */
  const useStackedHero = layout.viewportWidth < SPACE_DESKTOP_MIN_WIDTH;
  const useHeroOverlayCenter = useStackedHero && !isPhone;
  const heroOnPhoto = frame.isLargeFrame || useStackedHero;
  const scrollMinHeight = layout.viewportHeight;
  const columns = frame.isLargeFrame ? DESKTOP_FILL_ROW_CARD_COUNT : 2;
  const cardRows = Math.max(1, Math.ceil(pageCards.length / columns));
  /** Phone shows one row; medium shows up to two. Further cards scroll in the band. */
  const visibleRows = spaceVisibleCardRows(
    frame.isLargeFrame,
    isPhone,
    cardRows,
  );
  const cardsOverflow = cardRows > visibleRows;

  const header = (
    <View style={[styles.headerBar, headerBarStyle(frame, layout)]}>
      <SpacePageHeader
        spaceTitle={breadcrumbTitle || title}
        isRTL={isRTL}
        callSize={layout.callSize}
        wordmarkScale={layout.wordmarkScale}
        isCompact={isPhone}
        {...(frame.isLargeFrame
          ? { currentTypeSize: 20 * frame.frameScaleY }
          : {})}
        {...(hub.phoneNumber ? { phoneNumber: hub.phoneNumber } : {})}
        {...(hub.phoneIconUrl ? { phoneIconUrl: hub.phoneIconUrl } : {})}
        {...(hub.breadcrumbChevronIconUrl
          ? { breadcrumbChevronIconUrl: hub.breadcrumbChevronIconUrl }
          : {})}
      />
    </View>
  );

  const hero = (
    <SpaceDetailHeroBand
      layout={layout}
      frame={frame}
      isRTL={isRTL}
      isPhone={isPhone}
      title={title}
      body={body}
      ctaLabel={ctaLabel}
      heroUrl={heroUrl}
      titleTypeStyle={titleTypeStyle}
      bodyTypeStyle={bodyTypeStyle}
      onCta={onCta}
      useStackedHero={useStackedHero}
      useHeroOverlayCenter={useHeroOverlayCenter}
      heroOnPhoto={heroOnPhoto}
    />
  );

  const cardList = (
    <SpaceDetailCards
      cards={pageCards}
      useTwoColumnGrid={layout.useTwoColumnGrid}
      cardWidth={frame.detailCardWidth}
      cardHeight={frame.detailCardHeight}
      cardGap={frame.detailCardGap}
      cardRadius={frame.detailCardRadius}
      isRTL={isRTL}
      titleFontSize={labelSizes.titleFontSize}
      subtitleFontSize={labelSizes.subtitleFontSize}
      {...(onCardPress ? { onCardPress } : {})}
    />
  );

  // Header and hero stay fixed. Only the card band scrolls, including on mobile.
  const cardsBlock =
    frame.detailCardHeight * visibleRows +
    frame.detailCardGap * Math.max(0, visibleRows - 1);
  const bandPadTop = spaceCardsBandPadTop(frame.isLargeFrame, isPhone);
  const bandPadBottom = frame.isLargeFrame ? 0 : layout.padBottom;
  const cardsInset = frame.isLargeFrame
    ? frame.cardsRowInset
    : frame.detailPadSide;

  return (
    <View
      style={[
        styles.root,
        styles.lockedPage,
        { height: scrollMinHeight, maxHeight: scrollMinHeight },
      ]}
    >
      {header}
      {hero}
      <View
        style={[
          styles.cardsScrollBand,
          frame.isLargeFrame
            ? {
                height: frame.detailCardHeight,
                marginTop: frame.cardsGap,
              }
            : { height: bandPadTop + cardsBlock + bandPadBottom },
          {
            paddingTop: bandPadTop,
            paddingBottom: bandPadBottom,
          },
        ]}
      >
        <OverlayScrollView
          alwaysVisible={cardsOverflow}
          style={styles.cardsScrollFrame}
          scrollStyle={[
            styles.cardsScroll,
            {
              paddingLeft: cardsInset,
              paddingRight: cardsInset,
            },
          ]}
          contentContainerStyle={styles.cardsScrollContent}
          nestedScrollEnabled
        >
          {cardList}
        </OverlayScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: '100%',
    backgroundColor: bloomColors.bloomUniversalPage,
  },
  headerBar: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
    backgroundColor: bloomColors.bloomUniversalPage,
    paddingBottom: 16,
    zIndex: 2,
  },
  heroBand: {
    width: '100%',
    flexGrow: 1,
    flexShrink: 0,
    minHeight: 280,
    position: 'relative',
    overflow: 'hidden',
    justifyContent: 'flex-start',
  },
  heroImage: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
  heroFallback: {
    backgroundColor: bloomColors.bloomUniversalImageFallback,
  },
  pageScrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'transparent',
  },
  scroll: {
    flex: 1,
    zIndex: 1,
  },
  /** iPad / desktop: page itself does not scroll. */
  lockedPage: {
    overflow: 'hidden',
    flexDirection: 'column',
    width: '100%',
  },
  /** Card band under the hero. Height is the first screen of cards. */
  cardsScrollBand: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
    minHeight: 0,
    overflow: 'hidden',
    flexDirection: 'column',
    backgroundColor: bloomColors.bloomUniversalPage,
  },
  cardsScrollFrame: {
    flex: 1,
    width: '100%',
    minHeight: 0,
  },
  cardsScroll: {
    flex: 1,
    width: '100%',
    minHeight: 0,
    ...(Platform.OS === 'web' ? ({ overflow: 'scroll' } as object) : null),
  },
  cardsScrollContent: {
    flexGrow: 0,
    width: '100%',
  },
  /** Phone: column fills viewport; hero flexGrow absorbs height below cards. */
  scrollContentPhoneFill: Platform.select({
    web: {
      display: 'flex',
      flexDirection: 'column',
    },
    default: {},
  }) as ViewStyle,
  /** Let the hero band grow between header and cards (hub medium stack). */
  scrollContentMidHero:
    Platform.OS === 'web'
      ? ({
          flexGrow: 1,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
        } as ViewStyle)
      : { flexGrow: 1, flexShrink: 0 },
  heroBandMid: {
    flexGrow: 1,
    flexShrink: 0,
    minHeight: 280,
  },
  /**
   * Phone: flex hero fills tall viewports; in-flow copy block is vertically centered
   * (band grows with long copy so SE does not clip).
   */
  heroBandPhone: {
    flexGrow: 1,
    flexShrink: 0,
    minHeight: 280,
    justifyContent: 'center',
    alignItems: 'stretch',
  },
  heroCopyBandPhone: {
    flexGrow: 0,
    flexShrink: 0,
    width: '100%',
  },
  cardsSectionPhoneDock: {
    flexGrow: 0,
    flexShrink: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: FIGMA.headerGap,
    width: '100%',
  },
  headerBlock: {
    width: '100%',
  },
  headerRtl: {
    flexDirection: 'row-reverse',
  },
  headerWordmarkHit: {
    flexShrink: 0,
  },
  headerLeading: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
    minWidth: 0,
  },
  headerLeadingRtl: {
    flexDirection: 'row-reverse',
  },
  headerTrailing: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    flexShrink: 0,
  },
  headerTrailingRtl: {
    flexDirection: 'row-reverse',
  },
  callButtonWrap: {
    position: 'relative',
    flexShrink: 0,
    zIndex: 5,
    overflow: 'visible',
  },
  callButton: {
    backgroundColor: bloomColors.bloomUniversalPage,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  callButtonPressed: {
    opacity: 0.88,
  },
  callInfoTip: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 10,
    minWidth: 148,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: bloomColors.bloomUniversalPage,
    zIndex: 6,
    elevation: 6,
  },
  callInfoTipRtl: {
    // `right: 0` from the LTR tip would keep the popup growing off the left edge.
    right: 'auto',
    left: 0,
  },
  callInfoCaret: {
    position: 'absolute',
    top: -6,
    right: 16,
    width: 12,
    height: 12,
    backgroundColor: bloomColors.bloomUniversalPage,
    transform: [{ rotate: '45deg' }],
  },
  callInfoCaretRtl: {
    right: 'auto',
    left: 16,
  },
  callInfoLabel: {
    letterSpacing: 0.2,
    opacity: 0.7,
  },
  callInfoNumber: {
    letterSpacing: 0.2,
    marginTop: 6,
  },
  callInfoNumberLtr: {
    writingDirection: 'ltr',
    textAlign: 'left',
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 16,
    flexShrink: 1,
    minWidth: 0,
  },
  breadcrumbCompact: {
    marginTop: 12,
    width: '100%',
    flexShrink: 0,
  },
  breadcrumbRtl: {
    flexDirection: 'row-reverse',
  },
  breadcrumbChevronRtl: {
    transform: [{ scaleX: -1 }],
  },
  breadcrumbHomeHit: {
    flexShrink: 0,
  },
  breadcrumbHome: {
    textAlign: 'center',
  },
  breadcrumbCurrent: {
    flexShrink: 1,
    minWidth: 0,
  },
  heroCopyBand: {
    zIndex: 1,
    width: '100%',
    flexGrow: 1,
    justifyContent: 'flex-start',
  },
  /** Tablet mid — match Universal hub: copy vertically centered on hero photo. */
  spaceHeroCopyOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'stretch',
  },
  spaceHeroCopyInner: {
    width: '100%',
    alignItems: 'flex-start',
  },
  cardsSection: {
    width: '100%',
    backgroundColor: bloomColors.bloomUniversalPage,
    flexGrow: 0,
    flexShrink: 0,
  },
  heroCopy: {
    width: '100%',
    alignItems: 'flex-start',
  },
  heroCopyRtl: {
    alignItems: 'flex-end',
  },
  pageTitle: {
    letterSpacing: 0,
  },
  pageBody: {
    letterSpacing: 0,
    width: '100%',
  },
  pageBodyLarge: {
    fontFamily: 'Matter-Medium',
    fontWeight: '400',
  },
  cta: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: bloomColors.bloomUniversalPage,
  },
  ctaPressed: {
    opacity: 0.88,
  },
  ctaText: {
    textAlign: 'center',
  },
  cardsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    alignContent: 'flex-start',
  },
  cardsRowCompact: {
    alignItems: 'stretch',
  },
  /** Large desktop 1–3 cards: Figma-scaled width (no half-row stretch). */
  equalCardSlotFixed: {
    flexGrow: 0,
    flexShrink: 0,
    minWidth: 0,
  },
  compactCardSlot: {
    minWidth: 0,
  },
  compactCardPair: {
    flexGrow: 0,
    flexShrink: 0,
  },
  compactCardFull: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: '100%',
    width: '100%',
    maxWidth: '100%',
  },
});

export default BloomUniversalSpaceScreen;
